/**
 * Additional Custom Validation Functions
 * Examples of various validation types for the registry system
 */
import {
  registerValidation,
  type ValidationMeta,
  type ValidationResult,
} from './validation-registry';

// US Social Security Number Validation
export const usSsnValidationMeta: ValidationMeta = {
  type: 'ssn',
  name: 'US Social Security Number',
  format: 'XXX-XX-XXXX or XXXXXXXXX',
  example: '123-45-6789',
  description:
    'Validates US Social Security Numbers with area number, group number, and serial number validation.',
  country: 'United States',
  category: 'Identity Documents',
};

export function validateUsSsn(ssn: string): ValidationResult {
  // Remove any formatting
  const cleanSsn = ssn.replace(/[-\s]/g, '');

  // Check length
  if (cleanSsn.length !== 9) {
    return {
      isValid: false,
      message: '❌ SSN must be exactly 9 digits',
    };
  }

  // Check if all digits
  if (!/^\d{9}$/.test(cleanSsn)) {
    return {
      isValid: false,
      message: '❌ SSN can only contain digits',
    };
  }

  // Extract parts
  const areaNumber = cleanSsn.substring(0, 3);
  const groupNumber = cleanSsn.substring(3, 5);
  const serialNumber = cleanSsn.substring(5, 9);

  // Invalid area numbers
  if (
    areaNumber === '000' ||
    areaNumber === '666' ||
    areaNumber.startsWith('9')
  ) {
    return {
      isValid: false,
      message: '❌ Invalid area number in SSN',
    };
  }

  // Invalid group numbers
  if (groupNumber === '00') {
    return {
      isValid: false,
      message: '❌ Invalid group number in SSN',
    };
  }

  // Invalid serial numbers
  if (serialNumber === '0000') {
    return {
      isValid: false,
      message: '❌ Invalid serial number in SSN',
    };
  }

  return {
    isValid: true,
    message: '✅ Valid US Social Security Number',
    details: {
      formatted: `${areaNumber}-${groupNumber}-${serialNumber}`,
      areaNumber,
      groupNumber,
      serialNumber,
    },
  };
}

// Canadian Social Insurance Number (SIN) Validation
export const canadianSinValidationMeta: ValidationMeta = {
  type: 'sin',
  name: 'Canadian Social Insurance Number',
  format: 'XXX-XXX-XXX or XXXXXXXXX',
  example: '123-456-782',
  description:
    'Validates Canadian Social Insurance Numbers using the Luhn algorithm.',
  country: 'Canada',
  category: 'Identity Documents',
};

export function validateCanadianSin(sin: string): ValidationResult {
  // Remove any formatting
  const cleanSin = sin.replace(/[-\s]/g, '');

  // Check length
  if (cleanSin.length !== 9) {
    return {
      isValid: false,
      message: '❌ SIN must be exactly 9 digits',
    };
  }

  // Check if all digits
  if (!/^\d{9}$/.test(cleanSin)) {
    return {
      isValid: false,
      message: '❌ SIN can only contain digits',
    };
  }

  // First digit cannot be 0 or 8
  if (cleanSin.charAt(0) === '0' || cleanSin.charAt(0) === '8') {
    return {
      isValid: false,
      message: '❌ Invalid SIN: first digit cannot be 0 or 8',
    };
  }

  // Luhn algorithm validation
  let sum = 0;
  for (let i = 0; i < 8; i++) {
    let digit = parseInt(cleanSin.charAt(i));
    if (i % 2 === 1) {
      digit *= 2;
      if (digit > 9) {
        digit = Math.floor(digit / 10) + (digit % 10);
      }
    }
    sum += digit;
  }

  const checkDigit = (10 - (sum % 10)) % 10;
  const providedCheckDigit = parseInt(cleanSin.charAt(8));

  if (checkDigit !== providedCheckDigit) {
    return {
      isValid: false,
      message: '❌ Invalid SIN: check digit validation failed',
    };
  }

  return {
    isValid: true,
    message: '✅ Valid Canadian Social Insurance Number',
    details: {
      formatted: `${cleanSin.substring(0, 3)}-${cleanSin.substring(
        3,
        6
      )}-${cleanSin.substring(6, 9)}`,
      checkDigit,
    },
  };
}

// UK National Insurance Number Validation
export const ukNinoValidationMeta: ValidationMeta = {
  type: 'nino',
  name: 'UK National Insurance Number',
  format: 'LLNNNNNNL (e.g., AB123456C)',
  example: 'AB123456C',
  description:
    'Validates UK National Insurance Numbers with prefix and suffix letter validation.',
  country: 'United Kingdom',
  category: 'Identity Documents',
};

export function validateUkNino(nino: string): ValidationResult {
  // Remove spaces and convert to uppercase
  const cleanNino = nino.replace(/\s/g, '').toUpperCase();

  // Check format: 2 letters, 6 digits, 1 letter
  const ninoPattern = /^[A-Z]{2}\d{6}[A-Z]$/;
  if (!ninoPattern.test(cleanNino)) {
    return {
      isValid: false,
      message: '❌ Invalid NINO format. Must be 2 letters, 6 digits, 1 letter',
    };
  }

  // Extract parts
  const prefix = cleanNino.substring(0, 2);
  const number = cleanNino.substring(2, 8);
  const suffix = cleanNino.substring(8, 9);

  // Invalid prefixes
  const invalidPrefixes = [
    'BG',
    'GB',
    'NK',
    'KN',
    'TN',
    'NT',
    'ZZ',
    'AD',
    'DH',
    'FY',
    'IA',
    'IB',
    'IC',
    'ID',
    'IE',
    'IF',
    'IG',
    'IH',
    'II',
    'IJ',
    'IK',
    'IL',
    'IM',
    'IN',
    'IO',
    'IP',
    'IQ',
    'IR',
    'IS',
    'IT',
    'IU',
    'IV',
    'IW',
    'IX',
    'IY',
    'IZ',
    'QQ',
  ];

  if (invalidPrefixes.includes(prefix)) {
    return {
      isValid: false,
      message: `❌ Invalid NINO prefix: ${prefix}`,
    };
  }

  // Invalid suffixes
  const validSuffixes = ['A', 'B', 'C', 'D'];
  if (!validSuffixes.includes(suffix)) {
    return {
      isValid: false,
      message: `❌ Invalid NINO suffix: ${suffix}. Must be A, B, C, or D`,
    };
  }

  return {
    isValid: true,
    message: '✅ Valid UK National Insurance Number',
    details: {
      formatted: `${prefix} ${number.substring(0, 2)} ${number.substring(
        2,
        4
      )} ${number.substring(4, 6)} ${suffix}`,
      prefix,
      number,
      suffix,
    },
  };
}

// IBAN Validation
export const ibanValidationMeta: ValidationMeta = {
  type: 'iban',
  name: 'International Bank Account Number',
  format: 'Country code + check digits + bank account number',
  example: 'GB82 WEST 1234 5698 7654 32',
  description:
    'Validates International Bank Account Numbers using MOD-97 algorithm.',
  country: 'International',
  category: 'Financial',
};

export function validateIban(iban: string): ValidationResult {
  // Remove spaces and convert to uppercase
  const cleanIban = iban.replace(/\s/g, '').toUpperCase();

  // Check length (15-34 characters)
  if (cleanIban.length < 15 || cleanIban.length > 34) {
    return {
      isValid: false,
      message: '❌ IBAN length must be between 15 and 34 characters',
    };
  }

  // Check format: 2 letters + 2 digits + alphanumeric
  const ibanPattern = /^[A-Z]{2}\d{2}[A-Z0-9]+$/;
  if (!ibanPattern.test(cleanIban)) {
    return {
      isValid: false,
      message: '❌ Invalid IBAN format',
    };
  }

  // Extract parts
  const countryCode = cleanIban.substring(0, 2);
  const checkDigits = cleanIban.substring(2, 4);
  const bban = cleanIban.substring(4);

  // MOD-97 validation
  const rearranged = bban + countryCode + checkDigits;
  let numericString = '';

  for (let i = 0; i < rearranged.length; i++) {
    const char = rearranged.charAt(i);
    if (/[A-Z]/.test(char)) {
      // Convert A=10, B=11, ..., Z=35
      numericString += (char.charCodeAt(0) - 55).toString();
    } else {
      numericString += char;
    }
  }

  // Calculate mod 97
  let remainder = 0;
  for (let i = 0; i < numericString.length; i++) {
    remainder = (remainder * 10 + parseInt(numericString.charAt(i))) % 97;
  }

  if (remainder !== 1) {
    return {
      isValid: false,
      message: '❌ Invalid IBAN: check digit validation failed',
    };
  }

  return {
    isValid: true,
    message: '✅ Valid International Bank Account Number',
    details: {
      formatted: cleanIban.replace(/(.{4})/g, '$1 ').trim(),
      countryCode,
      checkDigits,
      bban,
    },
  };
}

// Test strings for each validation
export const usSsnTestStrings = [
  '123-45-6789', // Valid
  '123456789', // Valid (no formatting)
  '000-12-3456', // Invalid area number
  '666-12-3456', // Invalid area number
  '999-12-3456', // Invalid area number
  '123-00-3456', // Invalid group number
  '123-45-0000', // Invalid serial number
  '12-345-6789', // Wrong format
  '123-45-678a', // Contains letter
];

export const canadianSinTestStrings = [
  '123-456-782', // Valid
  '123456782', // Valid (no formatting)
  '046-454-286', // Valid
  '012-345-678', // Invalid (starts with 0)
  '812-345-678', // Invalid (starts with 8)
  '123-456-789', // Invalid check digit
  '123-45-6789', // Wrong length
  '12a-456-789', // Contains letter
];

export const ukNinoTestStrings = [
  'AB123456C', // Valid
  'JG 12 34 56 B', // Valid with spaces
  'BG123456A', // Invalid prefix
  'AB123456E', // Invalid suffix
  'A1234567B', // Wrong format
  '123456789', // No letters
  'ABCDEFGHI', // No numbers
];

export const ibanTestStrings = [
  'GB82 WEST 1234 5698 7654 32', // Valid UK
  'DE89370400440532013000', // Valid German
  'FR1420041010050500013M02606', // Valid French
  'GB82WEST12345698765432', // Valid (no spaces)
  'XX82WEST12345698765432', // Invalid country
  'GB82WEST123456987654', // Invalid check digits
  'GB82', // Too short
  'gb82west12345698765432', // Wrong case (should be handled)
];

// Register all validation functions
registerValidation(
  'us-ssn',
  usSsnValidationMeta,
  validateUsSsn,
  usSsnTestStrings
);
registerValidation(
  'canadian-sin',
  canadianSinValidationMeta,
  validateCanadianSin,
  canadianSinTestStrings
);
registerValidation(
  'uk-nino',
  ukNinoValidationMeta,
  validateUkNino,
  ukNinoTestStrings
);
registerValidation('iban', ibanValidationMeta, validateIban, ibanTestStrings);
